import type {
  ObjectType,
  SalesforceItem,
  SalesforceItemBase,
  RelatedObjectsMap,
} from './ParserTypes';
import { PARSER_BASE_TYPES } from './ParserTypes';
import type {
  SalesforceItemField,
  SalesforceItemFlow,
  ConfigurationItemRecordType,
} from './ConfigurationItemTypes';
import type { ElementProperties, FlowProperties } from '@server/documentation';
import { FlowTypes } from '@server/documentation';
import difference from 'lodash/difference';
import { setSfFieldName } from '../pages/rollups/rollupHelpers';
import { createStepIdentifier, createRecordTypeIdentifier } from '@server/pills';
import { isItemActive } from '@server/documentation';
import uniqBy from 'lodash/uniqBy';
import { mapMissingInternalIdToMetadataId } from '../documentation/helpers';
import type { LayoutsByObjectName } from '../documentation/salesforce-documentation-card/types';
import type {
  FieldParserProperties,
  ObjectParsedDataByType,
  RecordTypeParserProperties,
  ParsedRelatedElementsByType,
} from '@server/parser';
import {
  isNaclPicklistField,
  FIELD,
  PROCESS_BUILDER_FLOW,
  FLOW,
  APEX_CLASS,
  APEX_TRIGGER,
  VALIDATION_RULE,
  APPROVAL_PROCESS,
  WORKFLOW_RULE,
  RECORD_TYPE,
  OBJECT,
  LAYOUT,
} from '@server/parser';
import groupBy from 'lodash/groupBy';
import uniq from 'lodash/uniq';
import sum from 'lodash/sum';
import flatten from 'lodash/flatten';
import type { DocumentationData } from '../../types/documentation/documentation';
import type { DocumentationParsedData, ParsedFieldsByObject } from '../documentation/types';
import type { DocumentationPills } from '../documentation/types';
import { DocumentationTabTypes } from '../../types/enums/DocumentationTabTypes';
import { getOnlyAutomationsOfObject } from '../documentation/selected-object/filters/utils';

//create a human-readable description of the trigger and action
const getFlowDescription = (rule: FlowProperties) => {
  let flowDescription = rule.flowType?.toString();

  if ('triggers' in rule && rule.flowType === FlowTypes.RECORD_TRIGGERED) {
    const triggersDescriptions =
      rule.triggers?.map((trigger) => {
        if ('runWhen' in trigger) {
          const { runWhen, triggerOn } = trigger;
          const part1 = FLOW_TERMS_MAP[runWhen];
          const part2 =
            runWhen === 'RecordBeforeDelete' ? '' : ` on ${FLOW_TERMS_MAP[triggerOn]} action`;
          return `${part1}${part2}`;
        } else return '';
      }) ?? [];
    const triggersDescriptionsAsString = triggersDescriptions.join(', ');
    flowDescription += `, runs ${triggersDescriptionsAsString}.`;
  }

  if ('triggers' in rule && rule.flowType === FlowTypes.SCHEDULED_TRIGGERED) {
    const triggersDescriptions =
      rule.triggers?.map((trigger) => {
        if ('schedule' in trigger) {
          const { frequency } = trigger.schedule;
          return ` ${FLOW_TERMS_MAP[frequency]}`;
        } else return '';
      }) ?? [];
    const triggersDescriptionsAsString = triggersDescriptions.join(' and ');
    flowDescription += `, runs ${triggersDescriptionsAsString}.`;
  }

  return flowDescription;
};

const FLOW_TERMS_MAP: Record<string, string> = {
  RecordAfterSave: 'after record is saved',
  RecordBeforeSave: 'before record is saved',
  RecordBeforeDelete: 'before record is deleted',
  Update: 'update',
  CreateAndUpdate: 'create and update',
  Create: 'create',
  Delete: 'delete',
  Daily: 'daily',
  Weekly: 'weekly',
  Once: 'once',
};

const createRecordTypeConfigurationItem = ({
  recordTypes,
  objectName,
  withReferencedObjects,
}: {
  recordTypes: RecordTypeParserProperties[];
  objectName: string;
  withReferencedObjects?: boolean;
}): SalesforceItem[] =>
  recordTypes.map((recordType) => {
    //attributions are included as part of calcConfigurationItemsFromParserResponse
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { attributions, objectName: _objectName, ...rest } = recordType;
    return {
      ...createConfigurationItemFromParserResponseItem({
        responseItem: rest,
        objectApiName: objectName,
        keyType: RECORD_TYPE,
        withReferencedObjects,
      }),
    } as ConfigurationItemRecordType;
  });

//These are rules that are associated to a single object
const OBJECT_LEVEL_UNIQUE_RULE_TYPES = [
  VALIDATION_RULE,
  APPROVAL_PROCESS,
  WORKFLOW_RULE,
  APEX_TRIGGER,
];

//These are rules that are not associated to any specific object
const ORG_LEVEL_UNIQUE_RULE_TYPES = difference(PARSER_BASE_TYPES, OBJECT_LEVEL_UNIQUE_RULE_TYPES);

export const automationTypes = [
  APPROVAL_PROCESS,
  FLOW,
  PROCESS_BUILDER_FLOW,
  WORKFLOW_RULE,
] as const;

export const calcPills = (documentationData: DocumentationData): DocumentationPills => {
  const { parsedObjects, sweepElements } = documentationData;
  const { automations, alerts, assignments, scheduledAssignments, matching, dedup } = sweepElements;
  const data: DocumentationPills = {};

  parsedObjects.forEach((objectData) => {
    const { name: objectApiName, objectElements, relatedElements } = objectData;

    const getOnlyActive = (item: any) => isItemActive(item);

    const apexClassesCount = objectElements[APEX_CLASS]?.filter(getOnlyActive).length ?? 0;
    const apexTriggersCount = objectElements[APEX_TRIGGER]?.filter(getOnlyActive).length ?? 0;

    data[objectApiName] = {
      [DocumentationTabTypes.VALIDATION_RULES]:
        objectElements[VALIDATION_RULE]?.filter(getOnlyActive).length ?? 0,
      [DocumentationTabTypes.SF_AUTOMATIONS]: sum(
        automationTypes.map((type) => objectElements[type]?.filter(getOnlyActive).length ?? 0),
      ),
      [DocumentationTabTypes.APEX]: apexClassesCount + apexTriggersCount,
      [DocumentationTabTypes.FIELDS]: objectElements[FIELD]?.length ?? 0,
      [DocumentationTabTypes.RECORD_TYPES]:
        objectElements[RECORD_TYPE]?.filter(getOnlyActive).length ?? 0,
      [DocumentationTabTypes.LAYOUTS]: objectElements[LAYOUT]?.length ?? 0,
      [DocumentationTabTypes.MATCHING_DEDUPE]:
        getOnlyAutomationsOfObject(
          [...(matching ?? []), ...(dedup ?? [])] as AutomationStructureNew[],
          objectApiName,
        )?.length ?? 0,
      [DocumentationTabTypes.SWEEP_AUTOMATIONS]:
        getOnlyAutomationsOfObject((automations ?? []) as AutomationStructureNew[], objectApiName)
          ?.length ?? 0,
      [DocumentationTabTypes.ASSIGNMENTS]:
        getOnlyAutomationsOfObject((assignments ?? []) as AutomationStructureNew[], objectApiName)
          ?.length ?? 0,
      [DocumentationTabTypes.SCHEDULED_ASSIGNMENTS]:
        getOnlyAutomationsOfObject(
          (scheduledAssignments ?? []) as AutomationStructureNew[],
          objectApiName,
        )?.length ?? 0,
      [DocumentationTabTypes.PLAYBOOK_ALERTS]:
        getOnlyAutomationsOfObject((alerts ?? []) as AutomationStructureNew[], objectApiName)
          ?.length ?? 0,
      [DocumentationTabTypes.CARDS_LIST]: 0,
      [DocumentationTabTypes.RELATED_OBJECTS]:
        flatten(
          Object.values(relatedElements ?? {}).map((value) => (Array.isArray(value) ? value : [])),
        )?.filter(getOnlyActive).length ?? 0,
      [DocumentationTabTypes.OTHER]: 0,
    };
  });

  return data;
};

export const calcLayouts = (data: ObjectParsedDataByType[]): LayoutsByObjectName => {
  const layouts: LayoutsByObjectName = {};

  data?.forEach((objectData) => {
    const { name: objectApiName, objectElements } = objectData;
    const _objectLayouts = objectElements[LAYOUT] ?? [];

    _objectLayouts.forEach((baseItem) => {
      if (!layouts[objectApiName]) {
        layouts[objectApiName] = {
          layouts: [],
          isLoading: false,
        };
      }

      layouts[objectApiName].layouts.push(
        createConfigurationItemFromParserResponseItem({
          responseItem: baseItem,
          objectApiName,
          keyType: LAYOUT,
        }),
      );
    });
  });

  return layouts;
};

export const createConfigurationItemFromParserResponseItem = ({
  responseItem,
  keyType,
  objectApiName,
  rtLevelAttributions,
  picklistAttributions,
  stageLabels,
  withReferencedObjects,
}: {
  responseItem: ElementProperties;
  keyType: string;
  objectApiName: string;
  rtLevelAttributions?: string[];
  picklistAttributions?: string[];
  stageLabels?: string[];
  withReferencedObjects?: boolean;
}): SalesforceItem => {
  const parentType = keyType;

  const getBaseTranslation = (): SalesforceItemBase => {
    const { annotations, objectName, ...rest } = responseItem;

    const referencedObjects = withReferencedObjects
      ? 'referencedObjects' in responseItem
        ? responseItem.referencedObjects
        : [objectApiName]
      : undefined;

    const { id } = mapMissingInternalIdToMetadataId(rest);

    return {
      ...rest,
      id,
      parentType,
      objectApiNames: [objectApiName ?? objectName],
      usedByRecordTypes: rtLevelAttributions?.length ? rtLevelAttributions : undefined,
      usedByPicklistValue: picklistAttributions?.length ? picklistAttributions : undefined,
      stagesNames: stageLabels?.length ? stageLabels : undefined,
      referencedObjects,
      isActive: isItemActive(responseItem),
      updatedAt: annotations?.changedAt,
      updatedBy: annotations?.changedBy,
      createdAt: annotations?.createdAt,
      createdBy: annotations?.createdBy,
    };
  };

  const base = getBaseTranslation();

  switch (parentType) {
    case FIELD:
      return {
        ...base,
        parentType: FIELD,
        fieldType: (responseItem as FieldParserProperties).fieldType ?? '',
        isRollup: false,
      } as SalesforceItemField;

    case FLOW:
      return {
        ...base,
        parentType: FLOW,
        flowDescription: getFlowDescription(responseItem as FlowProperties),
      } as SalesforceItemFlow;

    case PROCESS_BUILDER_FLOW:
      return {
        ...base,
        parentType: PROCESS_BUILDER_FLOW,
        flowDescription: getFlowDescription(responseItem as FlowProperties),
      } as SalesforceItemFlow;

    case OBJECT:
      return {
        ...base,
        objectApiNames: responseItem.name ? [responseItem.name] : [objectApiName],
      };
    default:
      return base;
  }
};

export const calcConfigurationItemsFromParserResponse = (
  data: ObjectParsedDataByType[],
  withReferencedObjects?: boolean,
) => {
  const configurationItems: SalesforceItem[] = [];

  data.forEach((objectData) => {
    const { objectElements, name: objectApiName } = objectData;
    const { [FIELD]: fields, [RECORD_TYPE]: recordTypes, ...rest } = objectElements;
    const picklistFields = fields?.filter((field) => isNaclPicklistField(field.fieldType));

    Object.entries(rest).forEach(([configurationItemType, configurationItemsOfType]) => {
      const _configurationItemType = configurationItemType;
      const isOrgLevelUnique = ORG_LEVEL_UNIQUE_RULE_TYPES.includes(_configurationItemType);

      configurationItemsOfType?.forEach((configurationItemOfType) => {
        const rtLevelAttributions: string[] = [];
        const picklistAttributions: string[] = [];
        const stageLabels: string[] = [];

        recordTypes?.forEach((rt) => {
          const hasRecordTypeAttributions = rt.attributions?.includes(
            configurationItemOfType.metadataId,
          );

          const recordTypeId = createRecordTypeIdentifier({
            recordTypeName: rt.name,
            objectName: objectApiName,
            funnelId: '',
          });

          if (hasRecordTypeAttributions) {
            rtLevelAttributions.push(recordTypeId);
          }
        });

        picklistFields?.forEach((field) => {
          field.picklistValues?.forEach((picklistValue) => {
            const attributionsForConfigurationItemType = picklistValue.attributions;

            if (
              attributionsForConfigurationItemType?.includes(configurationItemOfType.metadataId)
            ) {
              const picklistId = createStepIdentifier({
                objectName: objectApiName,
                leadingField: field.name,
                stepFullName: picklistValue.name,
              });
              picklistAttributions.push(picklistId);
              stageLabels.push(picklistValue.label);
            }
          });
        });

        const existingConfigurationItem = isOrgLevelUnique
          ? configurationItems.find(
              (configurationItem) =>
                configurationItem.metadataId === configurationItemOfType.metadataId,
            )
          : undefined;

        if (existingConfigurationItem) {
          if (!existingConfigurationItem.objectApiNames.includes(objectApiName)) {
            existingConfigurationItem.objectApiNames.push(objectApiName);
          }

          if (withReferencedObjects) {
            if (!existingConfigurationItem.referencedObjects) {
              existingConfigurationItem.referencedObjects = [];
            }
            if (!existingConfigurationItem.referencedObjects.includes(objectApiName)) {
              existingConfigurationItem.referencedObjects.push(objectApiName);
            }
          }

          if (rtLevelAttributions.length) {
            const oldRTUsage = existingConfigurationItem.usedByRecordTypes ?? [];
            existingConfigurationItem.usedByRecordTypes = oldRTUsage.concat(rtLevelAttributions);
          }

          if (picklistAttributions.length) {
            const oldPicklistUsage = existingConfigurationItem.usedByPicklistValue ?? [];
            const oldStageLabels = existingConfigurationItem.stagesNames ?? [];
            existingConfigurationItem.usedByPicklistValue =
              oldPicklistUsage.concat(picklistAttributions);
            existingConfigurationItem.stagesNames = oldStageLabels.concat(stageLabels);
          }
        } else {
          configurationItems.push(
            createConfigurationItemFromParserResponseItem({
              keyType: _configurationItemType,
              responseItem: configurationItemOfType,
              objectApiName,
              rtLevelAttributions,
              picklistAttributions,
              stageLabels,
              withReferencedObjects,
            }),
          );
        }
      });
    });
  });
  return configurationItems;
};

export const transformParsedFields = (
  data: ObjectParsedDataByType[],
  withReferencedObjects?: boolean,
): ParsedFieldsByObject => {
  return Object.fromEntries(
    data.map((item) => [
      item.name,
      item.objectElements[FIELD]?.map((field) => {
        //eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { picklistValues, ...rest } = field;
        return createConfigurationItemFromParserResponseItem({
          responseItem: rest,
          objectApiName: field.objectName,
          withReferencedObjects,
          keyType: FIELD,
        }) as SalesforceItem;
      }) ?? [],
    ]),
  );
};

export const calcParsedRecordTypes = (
  data: ObjectParsedDataByType[],
  withReferencedObjects?: boolean,
): SalesforceItem[] => {
  let recordTypesResult: SalesforceItem[] = [];

  data.forEach((objectData) => {
    const { name, objectElements } = objectData;
    const recordTypes = objectElements[RECORD_TYPE];
    if (recordTypes) {
      const parsedRecordTypes = createRecordTypeConfigurationItem({
        recordTypes,
        objectName: name,
        withReferencedObjects,
      });
      recordTypesResult = recordTypesResult.concat(parsedRecordTypes);
    }
  });

  return recordTypesResult;
};

export const calcObjects = (data: ObjectParsedDataByType[]) => {
  const parserObjects: ObjectType[] = [];

  data.forEach((objectData) => {
    const { name, favorite } = objectData;
    parserObjects.push({ name, favorite });
  });

  return parserObjects;
};

export const calcRelatedObjects = (data: ObjectParsedDataByType[]) => {
  const parserObjects: RelatedObjectsMap = {};

  data.forEach((objectData) => {
    const { relatedElements, name } = objectData;

    const _relatedObjects: ObjectParsedDataByType[] = [
      {
        name,
        favorite: false,
        objectElements: relatedElements ?? {},
        relatedElements: {} as ParsedRelatedElementsByType,
      },
    ];

    const configurationItems = calcConfigurationItemsFromParserResponse(_relatedObjects, true);
    const recordTypes = calcParsedRecordTypes(_relatedObjects, true);
    const fields = transformParsedFields(_relatedObjects, true);

    const parsedFieldsWithReferenceObjects = Object.entries(fields ?? {})
      .map(([key, values]) => {
        return values.map((value) => ({ ...value, referencedObjects: [key] }));
      })
      .flat();

    const groupSameFieldIds = groupBy(parsedFieldsWithReferenceObjects, 'id');
    const groupedFields: SalesforceItem[] = [];

    Object.keys(groupSameFieldIds).forEach((key) => {
      const firstField = groupSameFieldIds[key][0];
      let newItem = firstField;

      if (groupSameFieldIds[key].length > 1) {
        groupSameFieldIds[key].forEach((item) => {
          newItem = {
            ...firstField,
            referencedObjects: item.referencedObjects
              ? uniq([...(newItem.referencedObjects ?? []), ...(item.referencedObjects ?? [])])
              : newItem.referencedObjects,
          };
        });
      }

      groupedFields.push(newItem);
    });

    parserObjects[name] = [
      ...(configurationItems ?? []),
      ...(recordTypes ?? []),
      ...(groupedFields ?? []),
    ];
  });
  return parserObjects;
};

export const ruleByObjectName = (objectName: string) => (rule: SalesforceItem) =>
  rule.objectApiNames.includes(objectName);

export const transformResponse = (data: DocumentationData): DocumentationParsedData => {
  const { parsedObjects } = data;
  const layouts = calcLayouts(parsedObjects);
  const configurationItems = calcConfigurationItemsFromParserResponse(parsedObjects);
  const onlyActiveConfigurationItems = configurationItems.filter(isItemActive);
  const recordTypes = calcParsedRecordTypes(parsedObjects);
  const objectNames = parsedObjects.map((objectInfo) => objectInfo.name);
  const fields = transformParsedFields(parsedObjects);
  const pills = calcPills(data);
  const parserObjects = calcObjects(parsedObjects);
  const relatedObjects = calcRelatedObjects(parsedObjects);

  return {
    layouts,
    configurationItems,
    onlyActiveConfigurationItems,
    parsedRecordTypes: recordTypes,
    parsedObjectNames: objectNames,
    parsedFields: fields,
    parserObjects,
    relatedObjects,
    pills,
  };
};

//check if field usage was refreshed and display new one
//global parsed field state shouldn't be updated because once it's refreshed the data will be gone
export const mergeFields = (
  fields: ParsedFieldsByObject,
  fieldsFromObjectsParsedOnDemand: ParsedFieldsByObject,
  fieldAccurateUsage: {
    [objectName: string]: {
      [fieldId: string]: number;
    };
  },
) => {
  const mapNewUsage = (arr: SalesforceItem[], objectName: string) => {
    return arr?.map((field) => {
      const usage =
        fieldAccurateUsage?.[objectName]?.[setSfFieldName(field.name, objectName)] ??
        ('usage' in field ? field.usage : undefined) ??
        0;
      return {
        ...field,
        usage,
      };
    });
  };

  return [fields, fieldsFromObjectsParsedOnDemand].reduce((acc, current) => {
    Object.keys(current).forEach((key) => {
      if (!acc[key]) {
        acc[key] = mapNewUsage(current[key], key);
      } else {
        acc[key] = mapNewUsage(uniqBy(acc[key].concat(current[key]), 'id'), key);
      }
    });
    return acc;
  }, {});
};
